/**
 * Pre-split text wrapped as an indexed line collection.
 *
 * Created once per diff input in the store; downstream renderers (virtual
 * list, SplitView) take indexed reads against this rather than re-splitting
 * the source string on every render cycle.
 *
 * Matches `.split("\n")` semantics: a single trailing "\n" does NOT produce
 * an extra empty line (so "a\nb\n" yields ["a", "b"]).
 */
export class Lines {
  private constructor(private readonly arr: readonly string[]) {}

  static fromString(text: string): Lines {
    if (text.length === 0) return new Lines([]);
    const arr = text.split("\n");
    // Drop the trailing empty caused by a final "\n" — match Rust str::lines().
    if (arr.length > 0 && arr[arr.length - 1] === "") {
      arr.pop();
    }
    return new Lines(arr);
  }

  static fromArray(arr: readonly string[]): Lines {
    return new Lines(arr);
  }

  get count(): number {
    return this.arr.length;
  }

  /**
   * Indexed read. Out-of-range returns "" so the renderer never crashes on
   * placeholder rows that point past the end.
   */
  get(i: number): string {
    if (i < 0 || i >= this.arr.length) return "";
    return this.arr[i] ?? "";
  }

  /** Contiguous range. Clamps `end` to `count`; `start` is assumed in range. */
  slice(start: number, end: number): string[] {
    return this.arr.slice(start, Math.min(end, this.arr.length));
  }

  /**
   * Round-trips the original text (modulo the trailing-newline rule documented
   * on `fromString`). Used by downstream consumers that need to feed the full
   * source into a tokenizer.
   */
  toString(): string {
    return this.arr.join("\n");
  }
}
